export type Role =
  | 'hq_global_admin'
  | 'hq_admin'
  | 'employer'
  | 'employee'
  | 'contractor'
  | 'volunteer'
  | 'personal';

const ROLES: readonly Role[] = [
  'hq_global_admin',
  'hq_admin',
  'employer',
  'employee',
  'contractor',
  'volunteer',
  'personal',
];

export function isRole(value: unknown): value is Role {
  if (typeof value !== 'string') {
    return false;
  }

  return ROLES.includes(value as Role);
}

export type Permission =
  | 'schedule:manage'
  | 'schedule:read'
  | 'settings:manage'
  | 'learning:manage'
  | 'learning:read'
  | 'payments:manage'
  | 'payments:read'
  | 'recruiting:manage'
  | 'tasker:manage'
  | 'tasker:read'
  | 'users:create'
  | 'users:delete'
  | 'users:read'
  | 'users:update';

export type FeatureFlag = 'billing' | 'experimental-dashboard';

/**
 * App-market applications, by the `sys_name` the backend uses. A workspace
 * has a module only while it has rented the application and switched it on;
 * the backend reports which in the session and refuses the rest.
 */
export type AppModule =
  'scheduler' | 'timeTracking' | 'tasker' | 'payments' | 'learner' | 'pop' | 'recruiting';

const APP_MODULES: readonly AppModule[] = [
  'scheduler',
  'timeTracking',
  'tasker',
  'payments',
  'learner',
  // Payroll by Payout Partner, the employer-of-record rail.
  'pop',
  'recruiting',
];

export function isAppModule(value: unknown): value is AppModule {
  return typeof value === 'string' && APP_MODULES.includes(value as AppModule);
}

export interface AccessRequirement {
  readonly roles?: readonly Role[];
  readonly permissions?: readonly Permission[];
  /** None of these may be held: shows an entry only to those without them. */
  readonly withoutPermissions?: readonly Permission[];
  readonly featureFlags?: readonly FeatureFlag[];
  /** Every one of these applications must be on. */
  readonly apps?: readonly AppModule[];
  /** At least one of these applications must be on. */
  readonly anyApps?: readonly AppModule[];
}

/** Celeri's own administrators manage like an employer, as the backend now treats them. */
const HQ_ADMIN: readonly Permission[] = [
  'users:read',
  'users:create',
  'users:update',
  'users:delete',
  'settings:manage',
  'schedule:read',
  'schedule:manage',
  'tasker:read',
  'tasker:manage',
  'learning:read',
  'learning:manage',
];

const ROLE_PERMISSIONS: Readonly<Record<Role, readonly Permission[]>> = {
  hq_global_admin: HQ_ADMIN,
  hq_admin: HQ_ADMIN,
  employer: [
    'users:read',
    'users:create',
    'users:update',
    'users:delete',
    'settings:manage',
    'schedule:read',
    'schedule:manage',
    'tasker:read',
    'tasker:manage',
    'learning:read',
    'learning:manage',
    'payments:read',
    'payments:manage',
    'recruiting:manage',
  ],
  // The people directory is the employer's: workers do not get Users.
  employee: ['schedule:read', 'tasker:read', 'learning:read', 'payments:read'],
  // Workers: their own shifts, tasks and training.
  contractor: ['schedule:read', 'tasker:read', 'learning:read', 'payments:read'],
  volunteer: ['schedule:read', 'tasker:read', 'learning:read'],
  personal: [],
};

export function permissionsForRoles(roles: readonly Role[]): readonly Permission[] {
  const granted = new Set<Permission>();

  for (const role of roles) {
    for (const permission of ROLE_PERMISSIONS[role]) {
      granted.add(permission);
    }
  }

  return [...granted];
}
